import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import GraphActivityLanesBand, {
  GRAPH_BRUSH_HEIGHT,
  GRAPH_PLOT_MARGIN_LEFT,
  GRAPH_PLOT_MARGIN_RIGHT,
  GRAPH_Y_AXIS_WIDTH,
  STACKED_PLOT_HEIGHT,
  STACKED_BRUSH_ZONE_HEIGHT,
  graphActivityBandHeight,
  stackedGraphHeight,
} from "../FieldApp/GraphActivityLanesLayer";
import m from "moment";

const ZOOM_STEP = 0.1;

const FIVE_MINUTES_MS = 5 * 60 * 1000;

function getFullTimeRange(data) {
  if (!data || !data.length) return null;
  return {
    startMs: data[0].time,
    endMs: data[data.length - 1].time,
  };
}

function indicesForTimeRange(data, startMs, endMs) {
  if (!data || !data.length) {
    return { startIndex: 0, endIndex: 0 };
  }

  let startIndex = 0;
  let endIndex = data.length - 1;

  for (let index = 0; index < data.length; index += 1) {
    if (data[index].time >= startMs) {
      startIndex = index;
      break;
    }
  }

  for (let index = data.length - 1; index >= 0; index -= 1) {
    if (data[index].time <= endMs) {
      endIndex = index;
      break;
    }
  }

  if (endIndex < startIndex) {
    endIndex = startIndex;
  }

  return { startIndex, endIndex };
}

function clampTimeRange(range, full, minSpanMs) {
  if (!full) return range;

  let startMs = range.startMs;
  let endMs = range.endMs;
  let span = endMs - startMs;
  const minSpan = Math.min(minSpanMs, Math.max(full.endMs - full.startMs, 1));

  if (span < minSpan) {
    const mid = (startMs + endMs) / 2;
    startMs = mid - minSpan / 2;
    endMs = mid + minSpan / 2;
    span = minSpan;
  }

  if (startMs < full.startMs) {
    startMs = full.startMs;
    endMs = Math.min(full.endMs, full.startMs + span);
  }
  if (endMs > full.endMs) {
    endMs = full.endMs;
    startMs = Math.max(full.startMs, full.endMs - span);
  }

  return { startMs, endMs };
}

function zoomTimeRange(visible, full, anchorTime, zoomIn) {
  const visSpan = visible.endMs - visible.startMs;
  const fullSpan = full.endMs - full.startMs;
  const minSpan = Math.min(FIVE_MINUTES_MS, fullSpan / 40);

  if (zoomIn && visSpan <= minSpan) {
    return visible;
  }

  const factor = zoomIn ? 1 - ZOOM_STEP : 1 + ZOOM_STEP;
  const newSpan = Math.min(fullSpan, Math.max(minSpan, visSpan * factor));
  const rel =
    visSpan > 0 ? (anchorTime - visible.startMs) / visSpan : 0.5;
  const clampedRel = Math.max(0, Math.min(1, rel));

  let startMs = anchorTime - clampedRel * newSpan;
  let endMs = anchorTime + (1 - clampedRel) * newSpan;

  return clampTimeRange({ startMs, endMs }, full, minSpan);
}

function panTimeRange(visible, full, deltaMs) {
  const span = visible.endMs - visible.startMs;
  const startMs = visible.startMs + deltaMs;
  const endMs = visible.endMs + deltaMs;
  return clampTimeRange({ startMs, endMs }, full, span);
}

function timeRangeFromIndices(data, startIndex, endIndex) {
  if (!data.length) {
    return { startMs: 0, endMs: 0 };
  }
  const safeStart = Math.max(0, Math.min(startIndex, data.length - 1));
  const safeEnd = Math.max(safeStart, Math.min(endIndex, data.length - 1));
  return {
    startMs: data[safeStart].time,
    endMs: data[safeEnd].time,
  };
}

function buildPlotData(visibleData, startMs, endMs) {
  if (visibleData.length) {
    return visibleData;
  }
  if (startMs == null || endMs == null || endMs <= startMs) {
    return [];
  }
  return [
    {
      time: startMs,
      velocidade: null,
      timestamp: new Date(startMs).toISOString(),
      isGapAnchor: true,
    },
    {
      time: endMs,
      velocidade: null,
      timestamp: new Date(endMs).toISOString(),
      isGapAnchor: true,
    },
  ];
}

function brushPercents(full, visible) {
  if (!full || full.endMs <= full.startMs) {
    return { leftPct: 0, widthPct: 100 };
  }
  const span = full.endMs - full.startMs;
  return {
    leftPct: ((visible.startMs - full.startMs) / span) * 100,
    widthPct: ((visible.endMs - visible.startMs) / span) * 100,
  };
}

const LineGraph = ({
  data,
  refValue = 5,
  period,
  onClick,
  onRangeChange,
  focusBrushRange,
  activityLanes,
  onActivityIntervalClick,
  showActivityBand = false,
  brushOnlyZoom = false,
  formatChartTime,
}) => {
  const containerRef = useRef(null);
  const brushZoneRef = useRef(null);
  const brushTrackRef = useRef(null);
  const dataRef = useRef(data);
  const brushTimeRangeRef = useRef({ startMs: 0, endMs: 0 });
  const lastFocusTokenRef = useRef(null);
  const lastRangeNotifyRef = useRef(null);
  const brushPanRef = useRef({ active: false, startX: 0, startRange: null });
  const brushResizeRef = useRef({
    active: false,
    edge: null,
    startX: 0,
    startRange: null,
  });
  const [brushZoneMounted, setBrushZoneMounted] = useState(false);
  const [brushTimeRange, setBrushTimeRange] = useState({ startMs: 0, endMs: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [measuredActivityBandHeight, setMeasuredActivityBandHeight] =
    useState(null);
  const activityBandRef = useRef(null);
  const dragRef = useRef({ startX: 0, startRange: null });

  dataRef.current = data;

  const bindBrushZoneRef = useCallback((node) => {
    brushZoneRef.current = node;
    setBrushZoneMounted(Boolean(node));
  }, []);

  const bindBrushTrackRef = useCallback((node) => {
    brushTrackRef.current = node;
  }, []);

  const updateBrushTimeRange = useCallback((nextRange) => {
    const resolved =
      typeof nextRange === "function"
        ? nextRange(brushTimeRangeRef.current)
        : nextRange;
    brushTimeRangeRef.current = resolved;
    setBrushTimeRange(resolved);
  }, []);

  useEffect(() => {
    brushTimeRangeRef.current = brushTimeRange;
  }, [brushTimeRange]);

  const brushRange = indicesForTimeRange(
    data,
    brushTimeRange.startMs,
    brushTimeRange.endMs
  );

  useEffect(() => {
    if (!data.length) {
      lastRangeNotifyRef.current = null;
      updateBrushTimeRange({ startMs: 0, endMs: 0 });
      return;
    }

    const full = getFullTimeRange(data);
    updateBrushTimeRange((prev) => {
      if (!prev.startMs && !prev.endMs) {
        return full;
      }

      const minSpan = Math.min(
        FIVE_MINUTES_MS,
        (full.endMs - full.startMs) / 40
      );
      const clamped = clampTimeRange(prev, full, minSpan);
      if (
        clamped.startMs === prev.startMs &&
        clamped.endMs === prev.endMs
      ) {
        return prev;
      }
      return clamped;
    });
  }, [
    data.length,
    data[0] && data[0].time,
    data[data.length - 1] && data[data.length - 1].time,
    updateBrushTimeRange,
  ]);

  useEffect(() => {
    if (!focusBrushRange || !data.length) {
      return;
    }

    const token = focusBrushRange.token;
    if (token != null) {
      if (lastFocusTokenRef.current === token) {
        return;
      }
      lastFocusTokenRef.current = token;
      const full = getFullTimeRange(data);
      if (full) {
        updateBrushTimeRange(full);
      }
      return;
    }

    if (
      focusBrushRange.startIndex == null ||
      focusBrushRange.endIndex == null
    ) {
      return;
    }

    updateBrushTimeRange(
      timeRangeFromIndices(
        data,
        focusBrushRange.startIndex,
        focusBrushRange.endIndex
      )
    );
  }, [
    focusBrushRange && focusBrushRange.token,
    focusBrushRange && focusBrushRange.startIndex,
    focusBrushRange && focusBrushRange.endIndex,
    data,
    updateBrushTimeRange,
  ]);

  useEffect(() => {
    if (!onRangeChange || data.length === 0) return;

    const payload = {
      startIndex: brushRange.startIndex,
      endIndex: brushRange.endIndex,
      startTimeMs: brushTimeRange.startMs,
      endTimeMs: brushTimeRange.endMs,
    };
    const notifyKey = [
      payload.startIndex,
      payload.endIndex,
      payload.startTimeMs,
      payload.endTimeMs,
    ].join(":");
    if (lastRangeNotifyRef.current === notifyKey) {
      return;
    }
    lastRangeNotifyRef.current = notifyKey;
    onRangeChange(payload);
  }, [
    brushRange.startIndex,
    brushRange.endIndex,
    brushTimeRange.startMs,
    brushTimeRange.endMs,
    data.length,
    onRangeChange,
  ]);

  const applyWheelZoom = useCallback(
    (clientX, rect, zoomIn) => {
      const series = dataRef.current;
      const full = getFullTimeRange(series);
      const visible = brushTimeRangeRef.current;
      if (!full || !series.length || full.endMs <= full.startMs) return;
      if (!rect || !rect.width) return;

      const cursorRatio = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width)
      );
      const anchorTime =
        full.startMs + cursorRatio * (full.endMs - full.startMs);
      const nextRange = zoomTimeRange(visible, full, anchorTime, zoomIn);
      if (
        nextRange.startMs === visible.startMs &&
        nextRange.endMs === visible.endMs
      ) {
        return;
      }
      updateBrushTimeRange(nextRange);
    },
    [updateBrushTimeRange]
  );

  const getBrushTrackRect = useCallback(() => {
    if (brushTrackRef.current) {
      return brushTrackRef.current.getBoundingClientRect();
    }
    if (brushZoneRef.current) {
      return brushZoneRef.current.getBoundingClientRect();
    }
    return null;
  }, []);

  const handleWheel = useCallback(
    (e) => {
      if (
        !brushOnlyZoom &&
        e.target &&
        e.target.closest(".line-graph-brush-zone")
      ) {
        return;
      }
      if (brushOnlyZoom) return;
      e.preventDefault();
      applyWheelZoom(e.clientX, containerRef.current.getBoundingClientRect(), e.deltaY < 0);
    },
    [brushOnlyZoom, applyWheelZoom]
  );

  const handleBrushWheel = useCallback(
    (e) => {
      if (!brushZoneRef.current || !brushZoneRef.current.contains(e.target)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const rect = getBrushTrackRect();
      if (!rect) return;
      applyWheelZoom(e.clientX, rect, e.deltaY < 0);
    },
    [applyWheelZoom, getBrushTrackRect]
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (e.target && e.target.closest(".field-app-graph-activity-band")) return;
      if (e.target && e.target.closest(".line-graph-brush-zone")) return;
      const full = getFullTimeRange(dataRef.current);
      const visible = brushTimeRangeRef.current;
      if (!full || visible.endMs - visible.startMs >= full.endMs - full.startMs) {
        return;
      }
      setIsDragging(true);
      dragRef.current = {
        startX: e.clientX,
        startRange: { ...visible },
      };
    },
    []
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      const { startX, startRange } = dragRef.current;
      const rect = containerRef.current.getBoundingClientRect();
      const full = getFullTimeRange(dataRef.current);
      if (!full || !rect.width) return;

      const deltaX = e.clientX - startX;
      const deltaMs = (deltaX / rect.width) * (full.endMs - full.startMs);
      updateBrushTimeRange(panTimeRange(startRange, full, deltaMs));
    },
    [isDragging, updateBrushTimeRange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!brushOnlyZoom || !brushZoneMounted) return;

    const onMouseDown = (event) => {
      if (event.button !== 0) return;

      const traveller = event.target.closest(
        ".line-graph-custom-brush-traveller"
      );
      const slide = event.target.closest(".line-graph-custom-brush-selection");
      const track = event.target.closest(".line-graph-custom-brush");

      if (traveller) {
        event.preventDefault();
        event.stopPropagation();
        brushResizeRef.current = {
          active: true,
          edge: traveller.classList.contains(
            "line-graph-custom-brush-traveller--left"
          )
            ? "left"
            : "right",
          startX: event.clientX,
          startRange: { ...brushTimeRangeRef.current },
        };
        if (brushZoneRef.current) {
          brushZoneRef.current.classList.add("line-graph-brush-zone--panning");
        }
        return;
      }

      if (!slide && !track) return;

      event.preventDefault();
      event.stopPropagation();

      const full = getFullTimeRange(dataRef.current);
      const visible = brushTimeRangeRef.current;
      if (
        !full ||
        visible.endMs - visible.startMs >= full.endMs - full.startMs - 1
      ) {
        return;
      }

      brushPanRef.current = {
        active: true,
        startX: event.clientX,
        startRange: { ...visible },
      };
      if (brushZoneRef.current) {
        brushZoneRef.current.classList.add("line-graph-brush-zone--panning");
      }
    };

    const onMouseMove = (event) => {
      const rect = getBrushTrackRect();
      const full = getFullTimeRange(dataRef.current);
      if (!full || !rect || !rect.width) return;

      const resize = brushResizeRef.current;
      if (resize.active && resize.startRange) {
        const fullSpan = full.endMs - full.startMs;
        const deltaMs =
          ((event.clientX - resize.startX) / rect.width) * fullSpan;
        const minSpan = Math.min(FIVE_MINUTES_MS, fullSpan / 40);

        if (resize.edge === "left") {
          updateBrushTimeRange(
            clampTimeRange(
              {
                startMs: resize.startRange.startMs + deltaMs,
                endMs: resize.startRange.endMs,
              },
              full,
              minSpan
            )
          );
        } else {
          updateBrushTimeRange(
            clampTimeRange(
              {
                startMs: resize.startRange.startMs,
                endMs: resize.startRange.endMs + deltaMs,
              },
              full,
              minSpan
            )
          );
        }
        return;
      }

      const pan = brushPanRef.current;
      if (!pan.active || !pan.startRange) return;

      const deltaX = event.clientX - pan.startX;
      const deltaMs = (deltaX / rect.width) * (full.endMs - full.startMs);
      updateBrushTimeRange(panTimeRange(pan.startRange, full, deltaMs));
    };

    const onMouseUp = () => {
      const hadInteraction =
        brushPanRef.current.active || brushResizeRef.current.active;
      brushPanRef.current = { active: false, startX: 0, startRange: null };
      brushResizeRef.current = {
        active: false,
        edge: null,
        startX: 0,
        startRange: null,
      };
      if (hadInteraction && brushZoneRef.current) {
        brushZoneRef.current.classList.remove("line-graph-brush-zone--panning");
      }
    };

    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      if (brushZoneRef.current) {
        brushZoneRef.current.classList.remove("line-graph-brush-zone--panning");
      }
    };
  }, [
    brushOnlyZoom,
    brushZoneMounted,
    getBrushTrackRect,
    updateBrushTimeRange,
  ]);

  useEffect(() => {
    if (!brushOnlyZoom || !brushZoneMounted) return;

    document.addEventListener("wheel", handleBrushWheel, {
      passive: false,
      capture: true,
    });
    return () => {
      document.removeEventListener("wheel", handleBrushWheel, { capture: true });
    };
  }, [brushOnlyZoom, brushZoneMounted, handleBrushWheel]);

  useEffect(() => {
    if (brushOnlyZoom) return;
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [brushOnlyZoom, handleWheel]);

  useEffect(() => {
    if (brushOnlyZoom || !isDragging) return;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [brushOnlyZoom, isDragging, handleMouseMove, handleMouseUp]);

  const handleBrushChange = (range) => {
    if (brushPanRef.current.active || brushResizeRef.current.active) {
      return;
    }
    if (
      !range ||
      range.startIndex == null ||
      range.endIndex == null
    ) {
      return;
    }

    updateBrushTimeRange(
      timeRangeFromIndices(dataRef.current, range.startIndex, range.endIndex)
    );
  };

  const handleClick = (e) => {
    if (isDragging || typeof onClick !== "function") return;
    if (e && e.activePayload && e.activePayload[0]) {
      onClick(e.activePayload[0].payload);
    }
  };

  const visibleStartTime = brushTimeRange.startMs;
  const visibleEndTime = brushTimeRange.endMs;
  const visibleData = data.filter(
    (point) =>
      point.time >= visibleStartTime && point.time <= visibleEndTime
  );
  const plotData = buildPlotData(
    visibleData,
    visibleStartTime,
    visibleEndTime
  );
  const plotDomain =
    visibleStartTime != null && visibleEndTime != null
      ? [visibleStartTime, visibleEndTime]
      : ["dataMin", "dataMax"];

  const getXAxisTicks = () => {
    if (visibleStartTime == null || visibleEndTime == null) return undefined;
    const ONE_HOUR = 3600000;
    const timeRange = visibleEndTime - visibleStartTime;

    let interval;
    if (timeRange <= ONE_HOUR) interval = FIVE_MINUTES_MS;
    else if (timeRange <= 6 * ONE_HOUR) interval = FIVE_MINUTES_MS;
    else if (timeRange <= 24 * ONE_HOUR) interval = ONE_HOUR / 2;
    else interval = ONE_HOUR;

    const firstTick = Math.ceil(visibleStartTime / interval) * interval;
    const tickCount = Math.ceil((visibleEndTime - firstTick) / interval);
    return Array.from({ length: tickCount + 1 }, (_, i) => firstTick + i * interval);
  };

  const laneCount =
    showActivityBand && activityLanes && activityLanes.length
      ? activityLanes.length
      : 0;
  const stackedLayout = laneCount > 0;
  const useCustomBrush = brushOnlyZoom || stackedLayout;
  const plotHeight =
    stackedLayout || brushOnlyZoom ? STACKED_PLOT_HEIGHT : 300;

  useEffect(() => {
    if (!stackedLayout) {
      setMeasuredActivityBandHeight(null);
      return undefined;
    }

    const node = activityBandRef.current;
    if (!node) return undefined;

    const measure = () => {
      const nextHeight = Math.ceil(node.getBoundingClientRect().height);
      if (nextHeight > 0) {
        setMeasuredActivityBandHeight((previous) =>
          previous === nextHeight ? previous : nextHeight
        );
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    stackedLayout,
    laneCount,
    activityLanes,
    visibleStartTime,
    visibleEndTime,
  ]);

  const estimatedActivityBandHeight = graphActivityBandHeight(laneCount);
  const resolvedActivityBandHeight =
    measuredActivityBandHeight != null
      ? measuredActivityBandHeight
      : estimatedActivityBandHeight;
  const containerHeight = stackedLayout
    ? STACKED_PLOT_HEIGHT +
      resolvedActivityBandHeight +
      STACKED_BRUSH_ZONE_HEIGHT
    : useCustomBrush
    ? STACKED_PLOT_HEIGHT + STACKED_BRUSH_ZONE_HEIGHT
    : 300;
  const stackedMinHeight = stackedLayout ? stackedGraphHeight(laneCount) : null;
  const useStackedChrome = stackedLayout || useCustomBrush;
  const fullTimeRange = getFullTimeRange(data);
  const brushPlotData = data;
  const brushSelection = brushPercents(fullTimeRange, brushTimeRange);
  const isBrushZoomed =
    fullTimeRange &&
    brushTimeRange.endMs - brushTimeRange.startMs <
      fullTimeRange.endMs - fullTimeRange.startMs - 1;

  const formatAxisTime = useCallback(
    (time, variant) => {
      if (typeof formatChartTime === "function") {
        return formatChartTime(time, variant);
      }
      if (variant === "hm") return m(time).format("HH:mm");
      return m(time).format("DD/MM HH:mm");
    },
    [formatChartTime]
  );

  const brushChart = (
    <Brush
      dataKey="time"
      height={GRAPH_BRUSH_HEIGHT}
      stroke="#8884d8"
      startIndex={brushRange.startIndex}
      endIndex={brushRange.endIndex}
      travellerWidth={10}
      tickFormatter={(time) => formatAxisTime(time, "dmhm")}
      alwaysShowText
      onChange={handleBrushChange}
      padding={{ left: 0, right: 0 }}
    />
  );

  const renderSpeedDot = useCallback(
    (props) => {
      const { cx, cy, index } = props;
      if (cx == null || cy == null) return null;
      const curr = plotData[index];
      if (!curr || curr.isGapAnchor) return null;
      if (index === 0 || index === plotData.length - 1) {
        return (
          <circle
            cx={cx}
            cy={cy}
            r={3}
            fill="#8884d8"
            stroke="#8884d8"
            strokeWidth={1}
          />
        );
      }
      const prev = plotData[index - 1];
      if (!prev || prev.isGapAnchor || curr.time - prev.time < FIVE_MINUTES_MS * 0.85) {
        return null;
      }
      return (
        <circle
          cx={cx}
          cy={cy}
          r={3}
          fill="#8884d8"
          stroke="#8884d8"
          strokeWidth={1}
        />
      );
    },
    [plotData]
  );

  const mainChart = (
    <LineChart
      data={plotData}
      margin={{ top: 8, right: 30, left: 20, bottom: 16 }}
      onClick={handleClick}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        dataKey="time"
        scale="time"
        type="number"
        domain={plotDomain}
        allowDataOverflow
        tickFormatter={(time) => {
          const timeRange = visibleEndTime - visibleStartTime;
          if (timeRange <= 6 * 3600000) return formatAxisTime(time, "hm");
          if (period > 1) return formatAxisTime(time, "dmhm");
          return formatAxisTime(time, "hm");
        }}
        ticks={getXAxisTicks()}
        tickLine={{ stroke: "#888" }}
        axisLine={{ stroke: "#888" }}
      />
      <YAxis
        width={GRAPH_Y_AXIS_WIDTH}
        tick={{ fontSize: 11 }}
        domain={(() => {
          const values = plotData
            .map((d) => d.velocidade)
            .filter((v) => v != null);
          if (!values.length) return [0, Math.ceil(refValue + 1)];
          const max = Math.max(...values, refValue);
          const padding = Math.max(1, max * 0.1);
          return [0, Math.ceil(max + padding)];
        })()}
      />
      <Tooltip
        labelFormatter={(time) =>
          typeof formatChartTime === "function"
            ? formatChartTime(time, "tooltip")
            : new Date(time).toLocaleString()
        }
      />
      <Legend />
      <Line
        type="monotone"
        dataKey="velocidade"
        stroke="#8884d8"
        activeDot={{ r: 6 }}
        isAnimationActive={false}
        connectNulls={false}
        dot={renderSpeedDot}
      />
      <ReferenceLine
        y={0}
        stroke="#ccc"
        strokeWidth={1}
      />
      <ReferenceLine
        y={refValue}
        label="Velocidade máxima"
        stroke="red"
        strokeDasharray="3 3"
      />
      {!useCustomBrush ? brushChart : null}
    </LineChart>
  );

  const plotInsetLeft = GRAPH_PLOT_MARGIN_LEFT + GRAPH_Y_AXIS_WIDTH;
  const plotInsetRight = GRAPH_PLOT_MARGIN_RIGHT;

  return (
    <div
      ref={containerRef}
      className={
        useStackedChrome
          ? "line-graph-container line-graph-container--stacked"
          : "line-graph-container"
      }
      onMouseDown={brushOnlyZoom ? undefined : handleMouseDown}
      style={{
        width: "100%",
        height: useStackedChrome ? "auto" : containerHeight,
        minHeight: stackedMinHeight || undefined,
        cursor: brushOnlyZoom
          ? "default"
          : isDragging
          ? "grabbing"
          : isBrushZoomed
          ? "grab"
          : "default",
        ...(useStackedChrome
          ? {
              "--graph-plot-inset-left": `${plotInsetLeft}px`,
              "--graph-plot-inset-right": `${plotInsetRight}px`,
            }
          : {}),
      }}
    >
      <div
        className="line-graph-plot-zone"
        style={{ height: plotHeight }}
      >
        <ResponsiveContainer width="100%" height={plotHeight}>
          {mainChart}
        </ResponsiveContainer>
      </div>

      {stackedLayout ? (
        <div
          ref={activityBandRef}
          className="line-graph-activity-band-wrap"
        >
          <GraphActivityLanesBand
            activityLanes={activityLanes}
            visibleStartTime={visibleStartTime}
            visibleEndTime={visibleEndTime}
            onActivityIntervalClick={onActivityIntervalClick}
          />
        </div>
      ) : null}
      {useCustomBrush ? (
        <div
          ref={bindBrushZoneRef}
          className="line-graph-brush-zone"
          style={{ height: STACKED_BRUSH_ZONE_HEIGHT }}
        >
          <div
            ref={bindBrushTrackRef}
            className="line-graph-brush-track"
            style={{ height: STACKED_BRUSH_ZONE_HEIGHT }}
          >
            <ResponsiveContainer
              width="100%"
              height={STACKED_BRUSH_ZONE_HEIGHT}
            >
              <LineChart
                data={brushPlotData}
                margin={{ top: 2, right: 0, left: 0, bottom: 2 }}
              >
                <XAxis
                  dataKey="time"
                  type="number"
                  scale="time"
                  hide
                  domain={
                    fullTimeRange
                      ? [fullTimeRange.startMs, fullTimeRange.endMs]
                      : ["dataMin", "dataMax"]
                  }
                  allowDataOverflow
                />
                <YAxis hide domain={[0, 1]} width={0} />
                <Line
                  type="monotone"
                  dataKey="velocidade"
                  stroke="#8884d8"
                  strokeOpacity={0.35}
                  dot={{ r: 1, fill: "#8884d8", stroke: "#8884d8" }}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
            {fullTimeRange ? (
              <div className="line-graph-custom-brush" aria-hidden="true">
                <div
                  className="line-graph-custom-brush-selection"
                  style={{
                    left: `${brushSelection.leftPct}%`,
                    width: `${Math.max(brushSelection.widthPct, 0.4)}%`,
                  }}
                >
                  <button
                    type="button"
                    className="line-graph-custom-brush-traveller line-graph-custom-brush-traveller--left"
                    aria-label="Ajustar início do período"
                  />
                  <button
                    type="button"
                    className="line-graph-custom-brush-traveller line-graph-custom-brush-traveller--right"
                    aria-label="Ajustar fim do período"
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LineGraph;
